import { Component, Input, OnInit, ChangeDetectorRef, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { BasePaymentModalComponent } from '../base-payment-modal/base-payment-modal.component';
import { BankPaymentLine } from '../../models/bank-payment.model';
import { TranslateService } from '@ngx-translate/core';

/**
 * @Component Decorator that marks a class as an Angular component and provides configuration metadata that determines how the component should be processed, instantiated, and used at runtime.
 * @description Component for handling bank transfer payment forms within a modal dialog. It extends BasePaymentModalComponent to reuse common payment modal functionalities.
 */
@Component({
	selector: 'app-bank-transfer-payment-form',
	templateUrl: './bank-transfer-payment-form.component.html',
	styleUrls: ['./bank-transfer-payment-form.component.scss']
})
export class BankTransferPaymentFormComponent extends BasePaymentModalComponent implements OnInit, OnChanges {
	/**
	 * @Input Decorator that marks a class field as an input property and supplies configuration metadata.
	 * @description An array of master bank data used for populating bank selection options.
	 */
	@Input() masterBank: any[] = [];

	/**
	 * @constructor Initializes the component, injecting necessary services.
	 * @param {FormBuilder} fb Service for creating reactive forms.
	 * @param {ChangeDetectorRef} cdr Service for triggering change detection.
	 * @param {TranslateService} translate Service for handling translations.
	 */
	constructor(
		protected fb: FormBuilder,
		protected cdr: ChangeDetectorRef,
		private translate: TranslateService
	) {
		super(fb, cdr);
	}

	/**
	 * @method ngOnInit A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
	 * @description Initializes the component, sets up local form lines, and subscribes to value changes.
	 */
	ngOnInit(): void {
		if (!this.lines) {
			console.error(this.translate.instant('notifications.errorSummary'));
			this.lines = this.fb.array([]);
		}
		this.initLocalLines();
	}

	/**
	 * @method ngOnChanges A lifecycle hook that is called when any data-bound property of a directive changes.
	 * @param {SimpleChanges} changes Object containing the changed properties.
	 * @description Responds to changes in input properties, specifically 'masterBank' and 'lines'.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['masterBank']) {
			this.cdr.markForCheck();
		}
	}

	/**
	 * @method addBankTransferLine Adds a new, empty bank transfer line to the local form array.
	 * @param {boolean} [updateTotal=true] Whether to update the total display after adding the line.
	 */
	addBankTransferLine(updateTotal: boolean = true): void {
		if (!this.localLines) {
			this.initLocalLines();
		}
		this.addLineWithValidation(() => this.createEmptyLineGroup(), updateTotal);
	}

	/**
	 * @getter localLinesInvalid Checks if the local form array is invalid.
	 * @returns {boolean} True if the `localLines` FormArray is invalid, false otherwise.
	 */
	get localLinesInvalid(): boolean {
		return this.localLines?.invalid ?? true;
	}

	/**
	 * @method onAmountInput Handles the input event for the amount field in the local form.
	 * @param {object} event The event object containing originalEvent and value.
	 * @param {number} index The index of the line being edited.
	 * @description Delegates to the `onAmountInputLocal` method inherited from the base class.
	 */
	onAmountInput(event: { originalEvent: Event, value: number | null }, index: number): void {
		this.onAmountInputLocal(event, index);
	}

	/**
	 * @method getBankLabel Returns the appropriate bank name based on the current language.
	 * @param {any} item The bank item object.
	 * @returns {string} The localized bank name.
	 */
	getBankLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'ar' ? item.bank_name_ar :
			this.curentLang === 'he' ? item.bank_name_he :
				item.bank_name_en;
	}

	/**
	 * @method getLineControls Retrieves the form controls for a specific line in the local form array.
	 * @param {number} index The index of the line.
	 * @returns {{ [key: string]: AbstractControl }} An object containing the form controls for the specified line, or an empty object if the line doesn't exist.
	 */
	getLineControls(index: number): { [key: string]: AbstractControl } {
		const lineGroup = this.localLines.at(index) as FormGroup;
		return lineGroup ? lineGroup.controls : {};
	}

	/**
	 * @method createEmptyLineGroup Creates a new FormGroup representing an empty bank transfer line.
	 * @returns {FormGroup} A FormGroup instance with default values and validators for a new bank transfer line.
	 */
	createEmptyLineGroup(): FormGroup {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return this.fb.group({
			id: [null],
			payment_type: [4], // Assuming 4 represents Bank Transfer
			date: [today, Validators.required],
			amount: [null, [Validators.required, Validators.min(0.01)]],
			bank: [null],
			account: [''], 
		});
	}
}
